import React from 'react';
import { FileText, X, Paperclip } from 'lucide-react';
import { useDocumentBlobUrl } from '../hooks/useDocumentBlobUrl';
import { VoiceClipPlayer } from './VoiceClipPlayer';
import { useLanguage } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// ReminderAttachmentList — the files attached to a reminder (#386).
//
// ONE renderer for every surface that shows them: the Bell row of a fired
// reminder, the Notes & Reminders card (active and Past), and the reminder edit
// modal (the only place with a remove control). Each attachment comes from the
// BE serializer: {document_id, filename, kind: 'image'|'audio'|'document',
// available, unavailable_reason}.
//
//   * image    → a small thumbnail beside the name;
//   * audio    → the shared VoiceClipPlayer, fed a blob URL of the file (the
//                same player the chat uses, so a voice note looks like a voice
//                note everywhere);
//   * document → a file icon.
//
// Clicking a name opens it in the existing DocumentPreviewModal via `onOpen`.
// A file that is in Recently Deleted is SHOWN greyed with a note rather than as
// a dead link (restoring it brings it back); a permanently deleted file is gone
// from the list server-side.
//
// Every interactive element stops propagation: the Bell row and the reminder
// card are themselves clickable, and opening a file must not also open the
// reminder modal or follow the row's link.
// ─────────────────────────────────────────────────────────────────────────────

const stop = (e) => e.stopPropagation();

const VoiceAttachment = ({ att }) => {
    const { url } = useDocumentBlobUrl(att.document_id, true, { path: 'download' });
    if (!url) {
        return <div className="h-9 w-full rounded-lg bg-white/[0.04] animate-pulse" />;
    }
    return <VoiceClipPlayer segment={{ audio_url: url }} />;
};

const AttachmentRow = ({ att, onOpen, onRemove, t }) => {
    const available = att.available !== false;
    const isImage = available && att.kind === 'image';
    const { url: thumbUrl } = useDocumentBlobUrl(att.document_id, isImage, { path: 'thumbnail' });
    const name = att.filename || t('noteAttachmentUnnamed', 'file');

    return (
        <li
            data-testid="reminder-attachment"
            data-kind={att.kind || 'document'}
            data-available={available ? 'true' : 'false'}
            data-filename={att.filename || ''}
            className="min-w-0 max-w-full"
            onClick={stop}
        >
            <div className="flex items-center gap-1.5 min-w-0">
                <button
                    type="button"
                    data-testid="reminder-attachment-open"
                    disabled={!available}
                    onClick={(e) => { stop(e); if (available) onOpen?.(att); }}
                    title={available ? name : t('reminderAttachmentUnavailable', 'Unavailable (in Recently Deleted)')}
                    className={`min-w-0 flex items-center gap-1.5 text-[12px] transition-colors ${
                        available ? 'text-pai-secondary hover:text-pai-blue' : 'text-white/30 cursor-not-allowed line-through'
                    }`}
                >
                    {isImage && thumbUrl ? (
                        <img src={thumbUrl} alt="" className="w-5 h-5 rounded-chip object-cover flex-shrink-0" />
                    ) : (
                        <FileText size={13} className="flex-shrink-0 text-pai-meta" />
                    )}
                    <span className="min-w-0 truncate">{name}</span>
                </button>
                {!available && (
                    <span data-testid="reminder-attachment-unavailable" className="flex-shrink-0 text-[10px] text-white/35">
                        {t('reminderAttachmentUnavailable', 'Unavailable (in Recently Deleted)')}
                    </span>
                )}
                {onRemove && (
                    <button
                        type="button"
                        data-testid="reminder-attachment-remove"
                        onClick={(e) => { stop(e); onRemove(att); }}
                        aria-label={t('reminderAttachmentRemove', 'Remove attachment from this reminder')}
                        title={t('reminderAttachmentRemove', 'Remove attachment from this reminder')}
                        className="ml-auto flex-shrink-0 p-0.5 rounded-chip text-pai-meta transition-colors hover:text-pai-red"
                    >
                        <X size={12} />
                    </button>
                )}
            </div>
            {available && att.kind === 'audio' && (
                <div data-testid="reminder-attachment-voice" className="mt-1 max-w-sm">
                    <VoiceAttachment att={att} />
                </div>
            )}
        </li>
    );
};

const ReminderAttachmentList = ({ attachments, onOpen, onRemove, className = '' }) => {
    const { t } = useLanguage();
    if (!attachments || attachments.length === 0) return null;
    return (
        <div data-testid="reminder-attachments" className={`min-w-0 max-w-full ${className}`} onClick={stop}>
            <div className="flex items-center gap-1 text-[11px] text-pai-meta mb-1">
                <Paperclip size={11} className="flex-shrink-0" />
                <span data-testid="reminder-attachment-count">
                    {t('reminderAttachmentCount', '{n} attachments', { n: attachments.length })}
                </span>
            </div>
            <ul className="flex flex-col gap-1 min-w-0">
                {attachments.map((att) => (
                    <AttachmentRow
                        key={att.id || att.document_id}
                        att={att}
                        onOpen={onOpen}
                        onRemove={onRemove}
                        t={t}
                    />
                ))}
            </ul>
        </div>
    );
};

export default ReminderAttachmentList;
